import { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

const RTC_CONFIGURATION: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

const FRAME_INTERVAL_MS = 120;
const FRAME_MAX_WIDTH = 640;
const FRAME_QUALITY = 0.55;
const DISPLAY_FRAME_INTERVAL_MS = 180;
const DISPLAY_FRAME_MAX_WIDTH = 640;
const DISPLAY_FRAME_QUALITY = 0.5;
const VIDEO_MAX_BITRATE = 1_500_000;
const VIDEO_MAX_FRAMERATE = 24;
const WEBRTC_FALLBACK_RESUME_TIMEOUT_MS = 8000;
const WEBRTC_MAX_RETRIES = 3;

interface LivestreamPublisherProps {
  socket: Socket | null;
  sessionId: string | null;
}

export default function LivestreamPublisher({ socket, sessionId }: LivestreamPublisherProps) {
  const [statusText, setStatusText] = useState('Kamera wird vorbereitet...');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isPublishingEnabled, setIsPublishingEnabled] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const frameCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isSendingFrameRef = useRef(false);
  const isRemoteVideoActiveRef = useRef(false);
  const livestreamDisplayViewerCountRef = useRef(0);
  const lastFrameSentAtRef = useRef(0);
  const isNegotiatingWebRtcRef = useRef(false);
  const fallbackResumeTimeoutRef = useRef<number | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const viewerIdRef = useRef<string | null>(null);
  const webRtcRetryCountRef = useRef(0);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const displayPeerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const displayPendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const knownDisplayViewerIdsRef = useRef<Set<string>>(new Set());
  const connectedDisplayViewersRef = useRef<Set<string>>(new Set());
  const lastFluidStateRef = useRef<boolean | null>(null);
  const mediaReadyResolverRef = useRef<(() => void) | null>(null);
  const mediaReadyPromiseRef = useRef<Promise<void>>(new Promise((resolve) => {
    mediaReadyResolverRef.current = resolve;
  }));

  const closeDisplayConnection = (displayViewerId: string) => {
    const peerConnection = displayPeerConnectionsRef.current.get(displayViewerId);
    if (peerConnection) {
      peerConnection.close();
    }

    connectedDisplayViewersRef.current.delete(displayViewerId);

    displayPeerConnectionsRef.current.delete(displayViewerId);
    displayPendingCandidatesRef.current.delete(displayViewerId);
  };

  const closeAllDisplayConnections = () => {
    Array.from(displayPeerConnectionsRef.current.keys()).forEach((displayViewerId) => {
      closeDisplayConnection(displayViewerId);
    });
  };

  const attachLocalTracks = (peerConnection: RTCPeerConnection) => {
    const localStream = localStreamRef.current;
    if (!localStream) return;

    const existingTrackIds = new Set(
      peerConnection.getSenders().map((sender) => sender.track?.id).filter((trackId): trackId is string => Boolean(trackId)),
    );

    localStream.getTracks().forEach((track) => {
      if (!existingTrackIds.has(track.id)) {
        const sender = peerConnection.addTrack(track, localStream);
        if (track.kind === 'video') {
          const parameters = sender.getParameters();
          void sender.setParameters({
            ...parameters,
            degradationPreference: 'maintain-framerate',
            encodings: [
              {
                ...(parameters.encodings?.[0] || {}),
                maxBitrate: VIDEO_MAX_BITRATE,
                maxFramerate: VIDEO_MAX_FRAMERATE,
              },
            ],
          }).catch((error) => {
            console.warn('Could not apply video sender parameters:', error);
          });
        }
      }
    });
  };

  const flushPendingCandidates = async (peerConnection: RTCPeerConnection) => {
    if (!peerConnection.remoteDescription) return;

    while (pendingCandidatesRef.current.length > 0) {
      const nextCandidate = pendingCandidatesRef.current.shift();
      if (!nextCandidate) continue;
      await peerConnection.addIceCandidate(new RTCIceCandidate(nextCandidate));
    }
  };

  const clearFallbackResumeTimeout = () => {
    if (fallbackResumeTimeoutRef.current !== null) {
      window.clearTimeout(fallbackResumeTimeoutRef.current);
      fallbackResumeTimeoutRef.current = null;
    }
  };

  const emitFluidState = (fluid: boolean, detail: string) => {
    if (!socket || !sessionId) return;
    if (lastFluidStateRef.current === fluid) return;

    lastFluidStateRef.current = fluid;
    socket.emit('participant:livestream-fluid-state', {
      fluid,
      detail,
    });
  };

  const emitLivestreamState = (enabled: boolean) => {
    if (!socket || !sessionId) return;

    socket.emit('participant:livestream-state', {
      enabled,
    });
  };

  useEffect(() => {
    let cancelled = false;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: VIDEO_MAX_FRAMERATE, max: 30 },
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        localStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch((error) => {
            console.warn('Could not autoplay local livestream preview:', error);
          });
        }
        mediaReadyResolverRef.current?.();
        mediaReadyResolverRef.current = null;
        if (peerConnectionRef.current) {
          attachLocalTracks(peerConnectionRef.current);
        }
        displayPeerConnectionsRef.current.forEach((peerConnection) => {
          attachLocalTracks(peerConnection);
        });
        setStatusText('Kamera aktiv. Warte auf Leitstelle...');
      } catch (error) {
        console.error('Error starting camera:', error);
        setErrorText('Kamera konnte nicht geöffnet werden. Bitte Kamerazugriff erlauben.');
        setStatusText('Kamera nicht verfügbar');
      }
    };

    void startCamera();

    return () => {
      cancelled = true;
      closeAllDisplayConnections();
      peerConnectionRef.current?.close();
      peerConnectionRef.current = null;
      localStreamRef.current?.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!socket || !sessionId) return;

    const intervalId = window.setInterval(() => {
      if (!isPublishingEnabled) {
        return;
      }

      if (isRemoteVideoActiveRef.current && livestreamDisplayViewerCountRef.current <= 0) {
        return;
      }

      const hasDisplayViewers = livestreamDisplayViewerCountRef.current > 0;

      if (isNegotiatingWebRtcRef.current && !hasDisplayViewers) {
        return;
      }

      if (isSendingFrameRef.current) {
        return;
      }

      const videoElement = videoRef.current;
      if (!videoElement || videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        return;
      }

      const sourceWidth = videoElement.videoWidth;
      const sourceHeight = videoElement.videoHeight;
      if (!sourceWidth || !sourceHeight) {
        return;
      }

      const useDisplayProfile = hasDisplayViewers && isRemoteVideoActiveRef.current;
      const targetIntervalMs = useDisplayProfile ? DISPLAY_FRAME_INTERVAL_MS : FRAME_INTERVAL_MS;
      const now = Date.now();
      if (now - lastFrameSentAtRef.current < targetIntervalMs) {
        return;
      }

      const maxWidth = useDisplayProfile ? DISPLAY_FRAME_MAX_WIDTH : FRAME_MAX_WIDTH;
      const jpegQuality = useDisplayProfile ? DISPLAY_FRAME_QUALITY : FRAME_QUALITY;

      const scale = Math.min(1, maxWidth / sourceWidth);
      const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
      const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

      let canvas = frameCanvasRef.current;
      if (!canvas) {
        canvas = document.createElement('canvas');
        frameCanvasRef.current = canvas;
      }

      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
      }

      const context = canvas.getContext('2d');
      if (!context) return;

      context.drawImage(videoElement, 0, 0, targetWidth, targetHeight);
      const frame = canvas.toDataURL('image/jpeg', jpegQuality);

      isSendingFrameRef.current = true;
      lastFrameSentAtRef.current = now;
      socket.emit(
        'participant:livestream-frame',
        {
          frame,
          width: targetWidth,
          height: targetHeight,
        },
        () => {
          isSendingFrameRef.current = false;
        },
      );
    }, FRAME_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      isSendingFrameRef.current = false;
      lastFrameSentAtRef.current = 0;
      clearFallbackResumeTimeout();
    };
  }, [isPublishingEnabled, sessionId, socket]);

  useEffect(() => {
    if (!socket || !sessionId) return;

    const cleanupConnection = () => {
      peerConnectionRef.current?.close();
      peerConnectionRef.current = null;
      viewerIdRef.current = null;
      isRemoteVideoActiveRef.current = false;
      isNegotiatingWebRtcRef.current = false;
      webRtcRetryCountRef.current = 0;
      clearFallbackResumeTimeout();
      emitFluidState(false, 'not-connected');
    };

    const flushPendingDisplayCandidates = async (displayViewerId: string, peerConnection: RTCPeerConnection) => {
      if (!peerConnection.remoteDescription) return;

      const pendingCandidates = displayPendingCandidatesRef.current.get(displayViewerId) || [];
      while (pendingCandidates.length > 0) {
        const nextCandidate = pendingCandidates.shift();
        if (!nextCandidate) continue;
        await peerConnection.addIceCandidate(new RTCIceCandidate(nextCandidate));
      }

      if (pendingCandidates.length === 0) {
        displayPendingCandidatesRef.current.delete(displayViewerId);
      } else {
        displayPendingCandidatesRef.current.set(displayViewerId, pendingCandidates);
      }
    };

    const ensurePeerConnection = (viewerId: string) => {
      if (peerConnectionRef.current && viewerIdRef.current === viewerId) {
        return peerConnectionRef.current;
      }

      cleanupConnection();

      const peerConnection = new RTCPeerConnection(RTC_CONFIGURATION);
      peerConnectionRef.current = peerConnection;
      viewerIdRef.current = viewerId;
      attachLocalTracks(peerConnection);

      peerConnection.onicecandidate = (event) => {
        if (!event.candidate || !viewerIdRef.current) return;

        socket.emit('participant:livestream-signal', {
          viewerId: viewerIdRef.current,
          candidate: event.candidate.toJSON(),
        });
      };

      peerConnection.onconnectionstatechange = () => {
        const state = peerConnection.connectionState;
        if (state === 'connected') {
          setErrorText(null);
          isNegotiatingWebRtcRef.current = false;
          clearFallbackResumeTimeout();
          setStatusText('WebRTC verbunden');
          return;
        }

        if (state === 'connecting') {
          isNegotiatingWebRtcRef.current = true;
          scheduleFallbackResume();
          setStatusText('Verbinde zur Leitstelle...');
          return;
        }

        if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          isNegotiatingWebRtcRef.current = false;
          clearFallbackResumeTimeout();
          setStatusText('Verbindung beendet. Seite offen lassen oder QR erneut scannen.');
        }
      };

      return peerConnection;
    };

    const ensureDisplayPeerConnection = (displayViewerId: string) => {
      const existingPeerConnection = displayPeerConnectionsRef.current.get(displayViewerId);
      if (existingPeerConnection) {
        return existingPeerConnection;
      }

      const peerConnection = new RTCPeerConnection(RTC_CONFIGURATION);
      displayPeerConnectionsRef.current.set(displayViewerId, peerConnection);
      attachLocalTracks(peerConnection);

      peerConnection.onicecandidate = (event) => {
        if (!event.candidate) return;

        socket.emit('participant:livestream-signal', {
          viewerId: displayViewerId,
          candidate: event.candidate.toJSON(),
        });
      };

      peerConnection.onconnectionstatechange = () => {
        const state = peerConnection.connectionState;
        if (state === 'connected') {
          connectedDisplayViewersRef.current.add(displayViewerId);
          emitFluidState(true, 'display-webrtc-connected');
          return;
        }

        if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          closeDisplayConnection(displayViewerId);
          if (connectedDisplayViewersRef.current.size === 0) {
            emitFluidState(false, 'display-webrtc-disconnected');
          }
        }
      };

      return peerConnection;
    };

    const sendOffer = async (viewerId: string, iceRestart: boolean = false) => {
      await mediaReadyPromiseRef.current;

      let peerConnection = ensurePeerConnection(viewerId);
      if (peerConnection.signalingState !== 'stable') {
        cleanupConnection();
        peerConnection = ensurePeerConnection(viewerId);
      }

      const offer = await peerConnection.createOffer({ iceRestart });
      await peerConnection.setLocalDescription(offer);

      socket.emit('participant:livestream-signal', {
        viewerId,
        description: offer,
      });
    };

    const sendDisplayOffer = async (displayViewerId: string) => {
      await mediaReadyPromiseRef.current;

      let peerConnection = ensureDisplayPeerConnection(displayViewerId);
      if (peerConnection.signalingState !== 'stable') {
        closeDisplayConnection(displayViewerId);
        peerConnection = ensureDisplayPeerConnection(displayViewerId);
      }

      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      socket.emit('participant:livestream-signal', {
        viewerId: displayViewerId,
        description: offer,
      });
    };

    const scheduleFallbackResume = () => {
      clearFallbackResumeTimeout();
      fallbackResumeTimeoutRef.current = window.setTimeout(() => {
        if (!isRemoteVideoActiveRef.current) {
          isNegotiatingWebRtcRef.current = false;
          const viewerId = viewerIdRef.current;
          if (viewerId && webRtcRetryCountRef.current < WEBRTC_MAX_RETRIES) {
            webRtcRetryCountRef.current += 1;
            isNegotiatingWebRtcRef.current = true;
            setStatusText(`WebRTC langsam, neuer Versuch ${webRtcRetryCountRef.current}/${WEBRTC_MAX_RETRIES}...`);

            void sendOffer(viewerId, true)
              .then(() => {
                scheduleFallbackResume();
              })
              .catch((error) => {
                console.error('Error retrying WebRTC offer:', error);
                isNegotiatingWebRtcRef.current = false;
                setStatusText('Fallback-Bild aktiv. WebRTC-Verbindung braucht zu lange.');
              });
            return;
          }

          setStatusText('Fallback-Bild aktiv. WebRTC-Verbindung braucht zu lange.');
        }
      }, WEBRTC_FALLBACK_RESUME_TIMEOUT_MS);
    };

    const onViewerReady = (payload: { viewerId?: string }) => {
      const viewerId = String(payload?.viewerId || '').trim();
      if (!viewerId) return;
      if (!isPublishingEnabled) {
        setStatusText('Live-Stream ist durch Instructor pausiert.');
        return;
      }
      isNegotiatingWebRtcRef.current = true;
      webRtcRetryCountRef.current = 0;
      scheduleFallbackResume();
      setStatusText('Leitstelle verbunden, starte WebRTC...');

      void (async () => {
        try {
          await sendOffer(viewerId);
        } catch (error) {
          console.error('Error creating livestream offer:', error);
          setErrorText('WebRTC-Offer konnte nicht erstellt werden.');
        }
      })();
    };

    const onDisplayViewerReady = (payload: { viewerId?: string }) => {
      const displayViewerId = String(payload?.viewerId || '').trim();
      if (!displayViewerId) return;
      knownDisplayViewerIdsRef.current.add(displayViewerId);
      if (!isPublishingEnabled) return;

      void sendDisplayOffer(displayViewerId).catch((error) => {
        console.error('Error creating livestream offer for display viewer:', error);
      });
    };

    const onDisplayViewerLeft = (payload: { viewerId?: string }) => {
      const displayViewerId = String(payload?.viewerId || '').trim();
      if (!displayViewerId) return;

      knownDisplayViewerIdsRef.current.delete(displayViewerId);
      closeDisplayConnection(displayViewerId);
      if (connectedDisplayViewersRef.current.size === 0) {
        emitFluidState(false, 'display-viewer-left');
      }
    };

    const onSetEnabled = (payload: { enabled?: boolean }) => {
      const enabled = Boolean(payload?.enabled);
      setIsPublishingEnabled(enabled);
      emitLivestreamState(enabled);

      if (!enabled) {
        closeAllDisplayConnections();
        cleanupConnection();
        setStatusText('Live-Stream durch Instructor gestoppt.');
        emitFluidState(false, 'instructor-stopped');
        return;
      }

      setStatusText('Live-Stream durch Instructor gestartet. Warte auf Viewer...');

      // Re-negotiate for display viewers that stayed on the page during pause.
      knownDisplayViewerIdsRef.current.forEach((displayViewerId) => {
        void sendDisplayOffer(displayViewerId).catch((error) => {
          console.error('Error re-starting livestream offer for display viewer:', error);
        });
      });
    };

    const onVideoActive = (payload: { viewerId?: string; active?: boolean }) => {
      const viewerId = String(payload?.viewerId || '').trim();
      if (!viewerId || viewerId !== viewerIdRef.current) return;

      isRemoteVideoActiveRef.current = Boolean(payload.active);
      if (isRemoteVideoActiveRef.current) {
        isNegotiatingWebRtcRef.current = false;
        webRtcRetryCountRef.current = 0;
        clearFallbackResumeTimeout();
        setStatusText('WebRTC-Livebild aktiv');
        return;
      }

      scheduleFallbackResume();
    };

    const onDisplayViewersChanged = (payload: { count?: number }) => {
      const nextCount = Math.max(0, Number(payload?.count || 0));
      livestreamDisplayViewerCountRef.current = nextCount;
    };

    const onSignalFromInstructor = async (payload: { viewerId?: string; description?: RTCSessionDescriptionInit | null; candidate?: RTCIceCandidateInit | null }) => {
      const viewerId = String(payload?.viewerId || '').trim();
      if (!viewerId) return;

      try {
        if (payload.description?.type === 'offer') {
          isNegotiatingWebRtcRef.current = true;
          scheduleFallbackResume();
          setStatusText('Leitstelle angefragt, warte auf Kamera-Freigabe...');
          await mediaReadyPromiseRef.current;
        }

        const peerConnection = ensurePeerConnection(viewerId);

        if (payload.description) {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(payload.description));
          await flushPendingCandidates(peerConnection);
        }

        if (payload.candidate) {
          if (!peerConnection.remoteDescription) {
            pendingCandidatesRef.current.push(payload.candidate);
          } else {
            await peerConnection.addIceCandidate(new RTCIceCandidate(payload.candidate));
          }
        }
      } catch (error) {
        console.error('Error handling livestream signal from instructor:', error);
        setErrorText(`Livestream-Verbindung zur Leitstelle fehlgeschlagen: ${(error as Error).message}`);
      }
    };

    const onSignalFromDisplay = async (payload: { viewerId?: string; description?: RTCSessionDescriptionInit | null; candidate?: RTCIceCandidateInit | null }) => {
      const displayViewerId = String(payload?.viewerId || '').trim();
      if (!displayViewerId) return;

      try {
        let peerConnection = ensureDisplayPeerConnection(displayViewerId);

        if (payload.description) {
          if (payload.description.type === 'offer' && peerConnection.signalingState !== 'stable') {
            closeDisplayConnection(displayViewerId);
            peerConnection = ensureDisplayPeerConnection(displayViewerId);
          }

          await peerConnection.setRemoteDescription(new RTCSessionDescription(payload.description));
          await flushPendingDisplayCandidates(displayViewerId, peerConnection);
        }

        if (payload.candidate) {
          if (!peerConnection.remoteDescription) {
            const pendingCandidates = displayPendingCandidatesRef.current.get(displayViewerId) || [];
            pendingCandidates.push(payload.candidate);
            displayPendingCandidatesRef.current.set(displayViewerId, pendingCandidates);
          } else {
            await peerConnection.addIceCandidate(new RTCIceCandidate(payload.candidate));
          }
        }
      } catch (error) {
        console.error('Error handling livestream signal from display viewer:', error);
      }
    };

    socket.on('livestream:viewer-ready', onViewerReady);
    socket.on('livestream:display-viewer-ready', onDisplayViewerReady);
    socket.on('livestream:display-viewer-left', onDisplayViewerLeft);
    socket.on('livestream:signal-from-instructor', onSignalFromInstructor);
    socket.on('livestream:signal-from-display', onSignalFromDisplay);
    socket.on('livestream:video-active', onVideoActive);
    socket.on('livestream:display-viewers-changed', onDisplayViewersChanged);
    socket.on('livestream:set-enabled', onSetEnabled);

    socket.emit('participant:join-livestream', { sessionId }, (result: { success?: boolean; error?: string }) => {
      if (!result?.success) {
        setErrorText(result?.error || 'Livestream-Sitzung konnte nicht geöffnet werden.');
        return;
      }

      emitLivestreamState(isPublishingEnabled);
    });

    return () => {
      socket.emit('participant:leave-livestream');
      socket.off('livestream:viewer-ready', onViewerReady);
      socket.off('livestream:display-viewer-ready', onDisplayViewerReady);
      socket.off('livestream:display-viewer-left', onDisplayViewerLeft);
      socket.off('livestream:signal-from-instructor', onSignalFromInstructor);
      socket.off('livestream:signal-from-display', onSignalFromDisplay);
      socket.off('livestream:video-active', onVideoActive);
      socket.off('livestream:display-viewers-changed', onDisplayViewersChanged);
      socket.off('livestream:set-enabled', onSetEnabled);
      closeAllDisplayConnections();
      cleanupConnection();
    };
  }, [isPublishingEnabled, sessionId, socket]);

  return (
    <div className="min-h-screen bg-dispatch-dark px-4 py-6 text-white">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold">🎥 Kamera-Livestream</h1>
          <p className="mt-2 text-sm text-gray-400">Diese Seite offen lassen. Das Kamerabild wird live an die Leitstelle übertragen.</p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-700 bg-black shadow-xl">
          <video ref={videoRef} autoPlay playsInline muted className="aspect-video w-full object-cover" />
        </div>

        <div className="rounded-lg bg-gray-800 p-4">
          <p className="text-sm font-semibold text-white">Status</p>
          <p className="mt-1 text-sm text-gray-300">{statusText}</p>
          {errorText && <p className="mt-2 text-sm text-red-300">{errorText}</p>}
        </div>
      </div>
    </div>
  );
}